// web-staking-estimator-frontend/src/RequestAssistance.js
import React, { useEffect, useState, useRef } from "react";
import { apiPost } from "./lib/api";

export default function RequestAssistance({ visible, member, request, pin, lastEstimateId, nearestNone }) {
  const [comments, setComments] = useState("");
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [busy, setBusy] = useState(false); 
  const [uiCfg, setUiCfg] = useState({});
  const msgRef = useRef(null);

  // Load UI config once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/config", { headers: { Accept: "application/json" } });
        const j = await r.json();
        if (!cancelled) setUiCfg(j?.config || {});
      } catch {
        /* ignore; fallback below */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Focus/scroll to the visible banner messages
  useEffect(() => {
    // You can gate by `visible` too, but keep the hook unconditionally declared
    if (!visible) return;
    const el = msgRef.current;
    if (el && (err || ok)) {
      try { el.focus(); } catch {}
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
  }, [err, ok, visible]);

  async function onSend() {
    if (busy) return; 
    const missing = [];

    const hasPin = Number.isFinite(pin?.x) && Number.isFinite(pin?.y) && Number.isFinite(pin?.wkid);
    if (!hasPin) missing.push("drop a location pin on the map");    
    if (!member?.first_name) missing.push("first name");
    if (!member?.last_name)  missing.push("last name");
    if (!member?.email)      missing.push("email address");
    if (!member?.phone)      missing.push("telephone");
    else if (String(member?.phone).replace(/\D/g, "").length !== 10)
      missing.push("telephone (10 digits)");
    if (!comments)           missing.push("comments");
    // Service inputs are OPTIONAL when there is no nearby infrastructure
    if (!nearestNone) {
      if (!request?.svc_volts) missing.push("service volts");
      // Check HP for irrigation, amps for others
      const isIrrigation = (request?.svc_type || "").toLowerCase().includes('irrig');
      if (isIrrigation) {
        if (!Number.isFinite(request?.svc_hp) || request.svc_hp <= 0) missing.push("service horsepower");
      } else {
        if (!Number.isFinite(request?.svc_amps)) missing.push("service amps");
      }
    }

    if (missing.length) {
      setErr((uiCfg?.messages?.missingInputsPrefix || "Please add the missing inputs: ") + missing.join(", "));
      setOk(null);
      return;
    }

    setErr(null);
    setBusy(true); 
    try {
      await apiPost("/api/email/assistance", {
        member: {
          firstName: member.first_name, lastName: member.last_name,
          email: member.email, phone: member.phone
        },
        request: {
          volts: request.svc_volts, amps: request.svc_amps, hp: request.svc_hp,
          phase: request.svc_phase, ohug: request.svc_ohug,
        },
        pin, comments, lastEstimateId,
      });
      setOk(uiCfg?.messages?.assistanceSubmitted || "Your request has been submitted.");
      setComments("");
    } catch {
      setErr(uiCfg?.messages?.requestFailed || "We couldn't send your request right now. Please try again.");
      setOk(null);
    } finally {
      setBusy(false);
    }    
  }

  // Render guard AFTER hooks so hooks are never conditional
  if (!visible) return null;

  return (
    <div className="card requestAssistanceCard">
      <h3>Request Assistance</h3>

      {err && (
        <div ref={msgRef} className="banner banner-err" tabIndex={-1} role="alert" aria-live="assertive">
          {err}
        </div>
      )}

      {ok && !err && (
        <div ref={msgRef} className="banner banner-ok" tabIndex={-1} role="status" aria-live="polite">
          {ok}
        </div>
      )}

      <textarea
        rows={4}
        placeholder="Describe your situation or what you need help with…"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        className="formInput"
      />

      <div className="btn-row" style={{ marginTop: 8 }}>
        <button type="button" className="btn" onClick={onSend} disabled={busy}>
          {uiCfg?.ui?.requestAssistanceButtonText || "Contact Us Now"}
        </button>
      </div>
    </div>
  );
}
